import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  reload,
  sendEmailVerification,
  updateProfile,
} from "firebase/auth";
import { auth } from "../../firebase";
import { getSafeAccessError, platformAccessApi } from "../api/platformAccessApi";
import { useAccess } from "../auth/AccessProvider";
import AccessAlert from "../components/AccessAlert";
import AccessShell from "../components/AccessShell";
import RegistrationAccountForm from "../components/RegistrationAccountForm";
import RegistrationStepper from "../components/RegistrationStepper";
import VerificationPanel from "../components/VerificationPanel";
import PlatformAdminInvitationRegisterPage from "./PlatformAdminInvitationRegisterPage";

const ACCOUNT_FORM = "account_form";
const EMAIL_VERIFICATION_REQUIRED = "email_verification_required";
const CODE_ENTRY = "code_entry";
const COMPLETING_ACCESS = "completing_access";
const VERIFICATION_POLL_MS = 5000;

function getActionCodeSettings() {
  return { url: `${window.location.origin}/platform-admin/register`, handleCodeInApp: false };
}

function PlatformAdminApprovalRegisterPage() {
  const navigate = useNavigate();
  const { platform, refreshAccessContext, user } = useAccess();
  const [stage, setStage] = useState(auth.currentUser ? EMAIL_VERIFICATION_REQUIRED : ACCOUNT_FORM);
  const [displayName, setDisplayName] = useState(auth.currentUser?.displayName || "");
  const [request, setRequest] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const requestedForUser = useRef(null);
  const requestInFlightForUser = useRef(null);
  const verificationCheckInFlightForUser = useRef(null);
  const automaticRetryForUser = useRef({ uid: null, failures: 0, nextAttemptAt: 0 });
  const trackedUserUid = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const nextUid = user?.uid || null;
    if (trackedUserUid.current !== nextUid) {
      trackedUserUid.current = nextUid;
      requestedForUser.current = null;
      requestInFlightForUser.current = null;
      verificationCheckInFlightForUser.current = null;
      automaticRetryForUser.current = { uid: null, failures: 0, nextAttemptAt: 0 };
      setRequest(null);
      setCode("");
      setError("");
      setMessage("");
      setBusy(false);
      setDisplayName(user?.displayName || "");
      setStage(user ? EMAIL_VERIFICATION_REQUIRED : ACCOUNT_FORM);
      return;
    }
    if (!user) {
      setStage(ACCOUNT_FORM);
      return;
    }
    setDisplayName((current) => current || user.displayName || "");
    setStage((current) => current === ACCOUNT_FORM ? EMAIL_VERIFICATION_REQUIRED : current);
  }, [user]);

  useEffect(() => {
    if (platform?.enabled) navigate("/platform-admin/dashboard", { replace: true });
  }, [platform, navigate]);

  const createAccount = async ({ firstName, surname, email, password }) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const name = `${firstName} ${surname}`.trim();
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(credential.user, { displayName: name });
      await sendEmailVerification(credential.user, getActionCodeSettings());
      setDisplayName(name);
      setStage(EMAIL_VERIFICATION_REQUIRED);
      setMessage("Your account was created. Verify your email to request Platform access.");
    } catch (nextError) {
      setError(getSafeAccessError(nextError, "We could not create your account. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const requestApprovalCode = useCallback(async ({ automatic = false, skipReload = false } = {}) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      if (!automatic) navigate("/login", { replace: true });
      return false;
    }
    const uid = currentUser.uid;
    if (requestedForUser.current === uid) return true;
    if (requestInFlightForUser.current === uid) return false;
    requestInFlightForUser.current = uid;
    if (!automatic) {
      automaticRetryForUser.current = { uid: null, failures: 0, nextAttemptAt: 0 };
    }
    if (mounted.current) {
      setBusy(true);
      setError("");
      setMessage("");
    }
    try {
      if (!skipReload) await reload(currentUser);
      if (auth.currentUser?.uid !== uid) return false;
      if (!currentUser.emailVerified) {
        if (!automatic && mounted.current) {
          setError("Your email is not verified yet. Complete verification, then try again.");
        }
        return false;
      }
      await currentUser.getIdToken(true);
      const nextRequest = await platformAccessApi.requestPlatformAdminRegistrationCode(
        displayName || currentUser.displayName || currentUser.email,
      );
      if (!mounted.current || auth.currentUser?.uid !== uid) return false;
      requestedForUser.current = currentUser.uid;
      automaticRetryForUser.current = { uid: null, failures: 0, nextAttemptAt: 0 };
      setRequest(nextRequest);
      setStage(CODE_ENTRY);
      setMessage(nextRequest?.message || "An approval code was sent securely to the Platform Owner.");
      return true;
    } catch (nextError) {
      if (automatic) {
        const previous = automaticRetryForUser.current;
        const failures = previous?.uid === uid ? previous.failures + 1 : 1;
        const retryDelayMs = Math.min(60000, 5000 * (2 ** (failures - 1)));
        automaticRetryForUser.current = {
          uid,
          failures,
          nextAttemptAt: Date.now() + retryDelayMs,
        };
      }
      if (mounted.current) {
        setError(getSafeAccessError(nextError, "We could not request approval. Please try again."));
      }
      return false;
    } finally {
      if (requestInFlightForUser.current === uid) {
        requestInFlightForUser.current = null;
      }
      if (mounted.current) setBusy(false);
    }
  }, [displayName, navigate]);

  const checkEmailVerification = useCallback(async () => {
    if (stage !== EMAIL_VERIFICATION_REQUIRED || request || busy || !user) return;
    const uid = user.uid;
    const retry = automaticRetryForUser.current || {};
    if (
      requestedForUser.current === uid ||
      requestInFlightForUser.current === uid ||
      verificationCheckInFlightForUser.current === uid ||
      (retry.uid === uid && Date.now() < retry.nextAttemptAt)
    ) {
      return;
    }
    verificationCheckInFlightForUser.current = uid;
    try {
      await reload(user);
      if (
        !mounted.current ||
        auth.currentUser?.uid !== uid ||
        !user.emailVerified
      ) {
        return;
      }
      setMessage("Email verified. Requesting approval from the Platform Owner...");
      await requestApprovalCode({ automatic: true, skipReload: true });
    } catch (nextError) {
      // A transient Auth reload failure should not disable automatic progress.
      // The next visible poll, focus, or visibility event will retry safely.
    } finally {
      if (verificationCheckInFlightForUser.current === uid) {
        verificationCheckInFlightForUser.current = null;
      }
    }
  }, [busy, request, requestApprovalCode, stage, user]);

  useEffect(() => {
    if (stage !== EMAIL_VERIFICATION_REQUIRED || request) return undefined;
    const runCheck = () => {
      if (document.visibilityState === "visible") void checkEmailVerification();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") runCheck();
    };
    runCheck();
    const interval = window.setInterval(runCheck, VERIFICATION_POLL_MS);
    window.addEventListener("focus", runCheck);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", runCheck);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [checkEmailVerification, request, stage]);

  const resendVerification = async () => {
    if (!auth.currentUser) return;
    setBusy(true);
    setError("");
    try {
      await sendEmailVerification(auth.currentUser, getActionCodeSettings());
      setMessage("A new verification email has been sent.");
    } catch (nextError) {
      setError(getSafeAccessError(nextError, "We could not resend verification. Please wait and try again."));
    } finally {
      setBusy(false);
    }
  };

  const resendApprovalCode = async () => {
    if (!request?.requestId) return;
    setBusy(true);
    setError("");
    try {
      const nextRequest = await platformAccessApi.resendPlatformAdminRegistrationCode(request.requestId);
      setRequest((current) => ({ ...current, ...nextRequest }));
      setMessage(nextRequest?.message || "A replacement code was sent to the Platform Owner.");
    } catch (nextError) {
      setError(getSafeAccessError(nextError, "The code could not be resent yet. Please wait and try again."));
    } finally {
      setBusy(false);
    }
  };

  const completeRegistration = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the six-digit approval code supplied by the Platform Owner.");
      return;
    }
    setStage(COMPLETING_ACCESS);
    setBusy(true);
    setError("");
    try {
      const result = await platformAccessApi.completePlatformAdminRegistration(request.requestId, code);
      const context = await refreshAccessContext({ forceTokenRefresh: result?.requiresTokenRefresh !== false });
      if (!context?.platform?.enabled) throw new Error("Platform access could not be confirmed.");
      setCode("");
      navigate("/platform-admin/dashboard", { replace: true });
    } catch (nextError) {
      setStage(CODE_ENTRY);
      setError(getSafeAccessError(nextError, "The approval code could not be accepted. Check it and try again."));
    } finally {
      setBusy(false);
    }
  };

  const activeStep = stage === ACCOUNT_FORM ? 0 : stage === EMAIL_VERIFICATION_REQUIRED ? 1 : 2;

  return (
    <AccessShell
      eyebrow="Register"
      title={stage === CODE_ENTRY || stage === COMPLETING_ACCESS ? "Complete Platform access." : "Create your account."}
      description={
        stage === CODE_ENTRY || stage === COMPLETING_ACCESS
          ? "Enter the approval code supplied by the Platform Owner."
          : "Register as an AndUs Platform Administrator."
      }
      footer={<p>Already have an account? <Link to="/login">Sign in</Link></p>}
    >
      <RegistrationStepper activeStep={activeStep} />
      <AccessAlert tone="error">{error}</AccessAlert>
      <AccessAlert tone="success">{message}</AccessAlert>

      {stage === ACCOUNT_FORM ? (
        <RegistrationAccountForm onSubmit={createAccount} submitting={busy} />
      ) : null}

      {stage === EMAIL_VERIFICATION_REQUIRED ? (
        <VerificationPanel
          email={auth.currentUser?.email}
          onContinue={() => requestApprovalCode()}
          onResend={resendVerification}
          busy={busy}
          continueLabel="Verify and request approval"
        />
      ) : null}

      {stage === CODE_ENTRY || stage === COMPLETING_ACCESS ? (
        <form className="pa-form pa-code-form" onSubmit={completeRegistration}>
          <div className="pa-approval-note">
            <strong>Approval is controlled by the Platform Owner</strong>
            <p>The code is sent only to the designated owner at TPC Training and expires shortly.</p>
          </div>
          <label className="pa-field">
            <span>Six-digit approval code</span>
            <input
              className="pa-code-input"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              placeholder="000000"
              disabled={busy}
              required
            />
          </label>
          <button className="pa-primary-button" type="submit" disabled={busy || code.length !== 6}>
            <span>{busy ? "Confirming access…" : "Complete registration"}</span><span aria-hidden="true">›</span>
          </button>
          <button className="pa-text-button" type="button" onClick={resendApprovalCode} disabled={busy}>Resend approval code</button>
        </form>
      ) : null}
    </AccessShell>
  );
}

export default function PlatformAdminRegisterPage() {
  const [searchParams] = useSearchParams();
  const invitationToken = useMemo(
    () => String(searchParams.get("invite") || "").trim(),
    [searchParams],
  );
  if (invitationToken) {
    return <PlatformAdminInvitationRegisterPage token={invitationToken} />;
  }
  return <PlatformAdminApprovalRegisterPage />;
}
